import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useCart } from '../context/CartContext';
import { API_URL } from '../utils/config';

const Search = () => {
  const [searchParams] = useSearchParams();
  const queryParam = searchParams.get('q') || '';
  
  const [searchQuery, setSearchQuery] = useState(queryParam);
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasSearched, setHasSearched] = useState(false);
  const { addToCart } = useCart();

  useEffect(() => {
    if (queryParam) {
      performSearch(queryParam);
    }
  }, [queryParam]);

  const performSearch = async (query) => {
    if (!query.trim()) {
      setError('Please enter a search term');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setHasSearched(true);
      
      // FIXED: Changed from single quotes to backticks
      const response = await axios.get(`${API_URL}/api/products`);
      
      // Filter products that match the search query
      const filtered = response.data.filter(product => 
        product.name.toLowerCase().includes(query.toLowerCase()) ||
        product.description?.toLowerCase().includes(query.toLowerCase()) ||
        product.category?.toLowerCase().includes(query.toLowerCase()) ||
        product.brand?.toLowerCase().includes(query.toLowerCase())
      );
      
      setSearchResults(filtered);
      setLoading(false);
    } catch (error) {
      console.error('Error searching products:', error);
      setError('Failed to search products. Make sure the server is running!');
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    performSearch(searchQuery);
  };

  const handleAddToCart = (product) => {
    addToCart(product);
    alert(`${product.name} added to cart!`);
  };

  const getImageUrl = (imagePath) => {
    if (!imagePath) return '/img/products/f1.jpg';
    if (imagePath.startsWith('/uploads/')) {
      return `${API_URL}${imagePath}`;
    }
    return imagePath;
  };

  return (
    <div className="search-page">
      {/* Search Header */}
      <section id="page-header" className="search-header">
        <h2>Search Products</h2>
        <p>Find your perfect IoT and Smart Home devices</p>
      </section>

      {/* Search Bar */}
      <section className="search-section section-p1">
        <form onSubmit={handleSearch} className="search-form">
          <div className="search-input-group">
            <input
              type="text"
              placeholder="Search for products, categories, brands..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-input"
            />
            <button type="submit" className="search-button">
              <i className="fas fa-search"></i> Search
            </button>
          </div>
        </form>

        {hasSearched && (
          <div className="search-info">
            {loading ? (
              <p>Searching...</p>
            ) : (
              <p>
                {searchResults.length > 0 
                  ? `Found ${searchResults.length} result${searchResults.length !== 1 ? 's' : ''} for "${searchQuery}"`
                  : `No results found for "${searchQuery}"`
                }
              </p>
            )}
          </div>
        )}
      </section>

      {/* Search Results */}
      <section id="product1" className="section-p1">
        {loading ? (
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Searching products...</p>
          </div>
        ) : error ? (
          <div className="error-container">
            <p className="error-message">{error}</p>
            <button onClick={() => performSearch(searchQuery)} className="retry-btn">
              <i className="fas fa-redo"></i> Retry
            </button>
          </div>
        ) : hasSearched && searchResults.length === 0 ? (
          <div className="no-results">
            <i className="fas fa-search" style={{ fontSize: '4rem', color: '#ccc' }}></i>
            <h3>No products found</h3>
            <p>Try different keywords or browse our shop</p>
            <Link to="/shop">
              <button className="normal">Browse All Products</button>
            </Link>
          </div>
        ) : searchResults.length > 0 ? (
          <div className="pro-container">
            {searchResults.map((product) => (
              <div key={product._id} className="pro">
                <Link to={`/product/${product._id}`}>
                  <img 
                    src={getImageUrl(product.image)} 
                    alt={product.name}
                    onError={(e) => {
                      e.target.src = '/img/products/f1.jpg';
                    }}
                  />
                  <div className="des">
                    <span>{product.brand || 'Goldvines'}</span>
                    <h5>{product.name}</h5>
                    <div className="star">
                      <i className="fas fa-star"></i>
                      <i className="fas fa-star"></i>
                      <i className="fas fa-star"></i>
                      <i className="fas fa-star"></i>
                      <i className="fas fa-star"></i>
                    </div>
                    <h4>{product.price} Taka</h4>
                    {product.category && (
                      <p className="product-category">
                        <i className="fas fa-tag"></i> {product.category}
                      </p>
                    )}
                  </div>
                </Link>
                <button 
                  className="cart-btn"
                  onClick={() => handleAddToCart(product)}
                  disabled={product.stock === 0}
                >
                  <i className="fa fa-shopping-cart cart"></i>
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div className="search-prompt">
            <i className="fas fa-search" style={{ fontSize: '4rem', color: '#ccc' }}></i>
            <h3>Start Searching</h3>
            <p>Enter a keyword above to find products</p>
            
            <div className="popular-searches">
              <h4>Popular Searches:</h4>
              <div className="search-tags">
                <button onClick={() => { setSearchQuery('RGB'); performSearch('RGB'); }}>
                  RGB Controllers
                </button>
                <button onClick={() => { setSearchQuery('Smart Home'); performSearch('Smart Home'); }}>
                  Smart Home
                </button>
                <button onClick={() => { setSearchQuery('LED'); performSearch('LED'); }}>
                  LED Strips
                </button>
                <button onClick={() => { setSearchQuery('Sensor'); performSearch('Sensor'); }}>
                  Sensors
                </button>
                <button onClick={() => { setSearchQuery('IoT'); performSearch('IoT'); }}>
                  IoT Devices
                </button>
              </div>
            </div>
          </div>
        )}
      </section>

      {/* Newsletter Section */}
      <section id="newsletter" className="section-p1 section-m1">
        <div className="newstext">
          <h4>Sign Up For Newsletters</h4>
          <p>Get E-mail updates about our latest shop and <span>special offers.</span></p>
        </div>
        <div className="form">
          <input type="text" placeholder="Your email address" />
          <button className="normal">Sign Up</button>
        </div>
      </section>
    </div>
  );
};

export default Search;